import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';

import { TransferState } from '@modules/transfer-state/transfer-state';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';


/**
 * Server: Intercepts GET 200 responses and serializes them to be rehydrated by the browser
 * Browser: try to use the server-generated response to GET requests.
 *
 * Caveats:
 * - 3** response codes (like redirects) are not serialized.
 */
@Injectable()
export class TransferStateInterceptorService implements HttpInterceptor {
  static STATE_KEY_PREFIX = 'HTTP_TS';

  private static getRequestHash(req: HttpRequest<any>): string {
    const token = req.headers.get('Token') || '';
    return req.method + req.url + (req.params && req.params.toString()) + token;
  }

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              private transferState: TransferState) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.method.toUpperCase() !== 'GET') {  // don't cache anything except GET
      return next.handle(req);
    }

    const res = this.browserMaybeRehydrateResponse(req);
    if (res) {
      return Observable.of(res);
    }

    return next.handle(req)
      .map((result: HttpEvent<any>) => {
        if ((result instanceof HttpResponse) && result.status === 200) {
          const { body, url } = result;
          this.serverMaybeSaveResponse(req, { body, url });
        }
        return result;
      });
  }

  private browserMaybeRehydrateResponse(req: HttpRequest<any>): HttpEvent<any> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const key = TransferStateInterceptorService.STATE_KEY_PREFIX +
      TransferStateInterceptorService.getRequestHash(req);
    const result = this.transferState.get(key);
    if (!result) {
      return null;
    }

    this.transferState.set(key, null);  // unset the cache to let the response be refreshed
    const { body, url } = result;
    return new HttpResponse<any>({ body, url });
  }

  private serverMaybeSaveResponse(req: HttpRequest<any>, body: any) {
    if (!isPlatformServer(this.platformId)) {
      return;
    }

    const key = TransferStateInterceptorService.STATE_KEY_PREFIX +
      TransferStateInterceptorService.getRequestHash(req);
    this.transferState.set(key, body);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/http-interceptors/transfer-state-interceptor.service.ts